import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { OnboardingScenario, OnboardingStep, OnboardingState, FOProfileData, UserProfileData } from '@/types/onboarding'
import WelcomeStep from './WelcomeStep'
import FOConfirmationStep from './FOConfirmationStep'
import ProfileCompletionStep from './ProfileCompletionStep'
import ManagerOrientationStep from './ManagerOrientationStep'
import OnboardingCompleteStep from './OnboardingCompleteStep'
import { onboardingService, OnboardingStage } from '@/services/api/onboardingService'
import { profileService } from '@/services/api/profileService'
import toast from 'react-hot-toast'

interface OnboardingWizardProps {
  scenario: OnboardingScenario
  foData: FOProfileData
  initialProfileData?: Partial<UserProfileData>
  onComplete: (state: OnboardingState) => void
}

// Map frontend OnboardingStep to backend OnboardingStage (partial — only steps used in wizard flows)
const stepToStage: Partial<Record<OnboardingStep, OnboardingStage>> = {
  'welcome': OnboardingStage.WELCOME,
  'fo-confirmation': OnboardingStage.FO_CONFIRMATION,
  'profile-completion': OnboardingStage.PROFILE_SETUP,
  'manager-orientation': OnboardingStage.PLATFORM_TOUR,  // closest backend stage
  'complete': OnboardingStage.COMPLETED
}

// Map backend OnboardingStage to frontend OnboardingStep
const stageToStep: Partial<Record<OnboardingStage, OnboardingStep>> = {
  [OnboardingStage.WELCOME]: 'welcome',
  [OnboardingStage.FO_CONFIRMATION]: 'fo-confirmation',
  [OnboardingStage.PROFILE_SETUP]: 'profile-completion',
  [OnboardingStage.PLATFORM_TOUR]: 'manager-orientation',
  [OnboardingStage.OBJECTIVES_PLANNING]: 'complete',
  [OnboardingStage.COMPLETED]: 'complete'
}

export default function OnboardingWizard({
  scenario,
  foData,
  initialProfileData,
  onComplete
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('welcome')
  const [completedSteps, setCompletedSteps] = useState<OnboardingStep[]>([])
  const [isRestoring, setIsRestoring] = useState(true)
  const didRestore = useRef(false)
  const [onboardingState, setOnboardingState] = useState<OnboardingState>({
    scenario,
    currentStep: 'welcome',
    completedSteps: [],
    foData,
    profileData: initialProfileData || null,
    foDataConfirmed: false,
    dataCorrect: false,
    tourCompleted: false,
    tourSkipped: false,
    startedAt: new Date().toISOString()
  })

  // Define step flow based on scenario and manager status
  // Note: Platform tour removed - interactive tours now trigger on first visit to Employee/Manager Journey pages
  // Manager status is determined ONLY by having direct reports > 0 (not the isManager flag which may be stale)
  const getStepFlow = (): OnboardingStep[] => {
    const isManager = foData.directReports > 0
    
    switch (scenario) {
      case 'first-time-user':
        if (isManager) {
          return ['welcome', 'fo-confirmation', 'profile-completion', 'manager-orientation', 'complete']
        }
        return ['welcome', 'fo-confirmation', 'profile-completion', 'complete']
      
      case 'role-change':
      case 'campus-transfer':
        if (isManager) {
          return ['welcome', 'fo-confirmation', 'manager-orientation', 'complete']
        }
        return ['welcome', 'fo-confirmation', 'complete']
      
      case 'annual-profile-update':
        return ['welcome', 'profile-completion']
      
      default:
        if (isManager) {
          return ['welcome', 'fo-confirmation', 'manager-orientation']
        }
        return ['welcome', 'fo-confirmation']
    }
  }

  const stepFlow = getStepFlow()
  const currentStepIndex = stepFlow.indexOf(currentStep)

  // Restore progress from backend API on mount
  useEffect(() => {
    const restoreProgress = async () => {
      try {
        const status = await onboardingService.getStatus()
        if (status.current_stage && status.current_stage !== 'not_started') {
          const mappedStep = stageToStep[status.current_stage as OnboardingStage]
          if (mappedStep && stepFlow.includes(mappedStep)) {
            // Derive completed steps = all steps before the current one
            const resumeIndex = stepFlow.indexOf(mappedStep)
            const completed = stepFlow.slice(0, resumeIndex)
            didRestore.current = true
            setCurrentStep(mappedStep)
            setCompletedSteps(completed)
            setOnboardingState(prev => ({
              ...prev,
              currentStep: mappedStep,
              completedSteps: completed,
              foDataConfirmed: status.fo_data_confirmed,
              dataCorrect: status.fo_data_correct ?? false
            }))
          }
        }
      } catch {
        // API unavailable — start from welcome (default)
      } finally {
        setIsRestoring(false)
      }
    }
    restoreProgress()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const markStepComplete = (step: OnboardingStep) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps([...completedSteps, step])
    }
  }

  // Persist current stage to backend (fire-and-forget)
  const persistStage = useCallback((step: OnboardingStep) => {
    const stage = stepToStage[step]
    if (stage) {
      onboardingService.updateStage(stage).catch(() => {
        // Non-blocking — don't prevent navigation
      })
    }
  }, [])

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1
    if (nextIndex < stepFlow.length) {
      const nextStep = stepFlow[nextIndex]
      markStepComplete(currentStep)
      setCurrentStep(nextStep)
      setOnboardingState(prev => ({
        ...prev,
        currentStep: nextStep,
        completedSteps: [...prev.completedSteps, currentStep]
      }))
      persistStage(nextStep)
    } else {
      handleComplete()
    }
  }

  const goToPreviousStep = () => {
    const prevIndex = currentStepIndex - 1
    if (prevIndex >= 0) {
      const prevStep = stepFlow[prevIndex]
      setCurrentStep(prevStep)
      setOnboardingState(prev => ({
        ...prev,
        currentStep: prevStep
      }))
      persistStage(prevStep)
    }
  }

  const handleComplete = () => {
    const finalState: OnboardingState = {
      ...onboardingState,
      completedAt: new Date().toISOString(),
      completedSteps: stepFlow
    }
    persistStage('complete')
    onComplete(finalState)
  }

  // Step-specific handlers
  const handleFOConfirm = async () => {
    try {
      // Call API to save confirmation
      await onboardingService.confirmFOData({
        data_correct: true
      })
      
      // Update local state
      setOnboardingState(prev => ({
        ...prev,
        foDataConfirmed: true,
        dataCorrect: true
      }))
      
      toast.success('F&O data confirmed successfully!')
      goToNextStep()
    } catch (error) {
      console.error('Failed to confirm F&O data:', error)
      toast.error('Failed to save confirmation. Please try again.')
    }
  }

  const handleFOReportIncorrect = async (ticketInfo: string) => {
    try {
      const ticket = JSON.parse(ticketInfo)
      
      // Call API to save incorrect confirmation with escalation
      await onboardingService.confirmFOData({
        data_correct: false,
        escalation_reason: ticket.description,
        hr_ticket: ticket
      })
      
      // Update local state
      setOnboardingState(prev => ({
        ...prev,
        foDataConfirmed: false,
        dataCorrect: false,
        hrTicketId: ticket.ticketId
      }))
      
      toast.success('Your concerns have been reported to HR. You can continue onboarding.')
      goToNextStep()
    } catch (error) {
      console.error('Failed to report incorrect data:', error)
      toast.error('Failed to report issue. Please try again.')
    }
  }

  const handleTeamReportIncorrect = async (ticketInfo: string) => {
    try {
      const ticket = JSON.parse(ticketInfo)
      
      // Call API to report team structure issue
      await onboardingService.confirmFOData({
        data_correct: false,
        escalation_reason: ticket.description,
        hr_ticket: ticket
      })
      
      toast.success('Team structure issue reported to HR.')
    } catch (error) {
      console.error('Failed to report team issue:', error)
      toast.error('Failed to report issue. Please try again.')
    }
  }

  const handleProfileSave = async (profileData: Partial<UserProfileData>) => {
    try {
      // Filter out empty/incomplete qualifications (professional certs don't need institution)
      const validQualifications = (profileData.qualifications || [])
        .filter(q => q.type === 'professional' ? q.professionalBody : (q.institution && q.institution.trim()))
        .map(q => ({
          qualification_type: q.type,
          institution: q.institution || '',
          country: q.country || 'south_africa',
          
          // Academic details
          qualification_name: q.qualificationName?.trim() || undefined,
          cesm_category: q.cesmCategory || undefined,
          field_of_study: q.fieldOfStudy?.trim() || undefined,

          // NQF
          nqf_level: q.nqfLevel || undefined,

          // Completion status
          year_completed: q.yearCompleted || undefined,
          in_progress: q.inProgress || false,
          expected_completion_year: q.expectedCompletionYear || undefined,

          // International
          international_equivalency: q.internationalEquivalency || undefined,
          saqa_evaluated: q.saqaEvaluated || false,
          saqa_reference_number: q.saqaReferenceNumber || undefined,

          // Professional cert
          professional_body: q.professionalBody || undefined,
          membership_number: q.membershipNumber || undefined,
          membership_status: q.membershipStatus || undefined,

          // Research component
          thesis: q.thesis || false,
          thesis_title: q.thesisTitle || undefined,

          // Verification
          verification_document_url: q.verificationDocument || undefined
        }));
      
      // Filter out empty/incomplete career entries
      const validCareerHistory = (profileData.careerHistory || [])
        .filter(c => c.organization && c.organization.trim() && c.position && c.position.trim() && c.startDate)
        .map(c => ({
          organization: c.organization,
          position: c.position,
          start_date: c.startDate,
          end_date: c.endDate,
          organization_type: c.organizationType,
          direct_reports: c.directReports,
          extended_team_size: c.extendedTeamSize,
          budget_responsibility: c.budgetResponsibility,
          budget_currency: c.budgetCurrency,
          key_achievements: c.keyAchievements || [],
          skills_developed: c.skillsDeveloped || []
        }));
      
      // Convert and save profile data to backend
      await profileService.saveCompleteProfile({
        qualifications: validQualifications,
        career_history: validCareerHistory,
        professional_context: {
          areas_of_expertise: profileData.professionalContext?.areasOfExpertise || [],
          language_proficiencies: (profileData.professionalContext?.languageProficiencies || []).map(l => ({
            language: l.language,
            level: l.proficiency || 'intermediate'
          })),
          geographic_experience: profileData.professionalContext?.geographicExperience || [],
          industry_experience: profileData.professionalContext?.industryExperience || [],
          completion_status: (profileData.completionStatus || 'complete') as any
        }
      })
      
      // Update local state with saved profile data
      setOnboardingState(prev => ({
        ...prev,
        profileData
      }))
      
      toast.success('Profile saved successfully!')
      goToNextStep()
    } catch (error) {
      console.error('Failed to save profile:', error)
      // Only show toast for non-HTTP errors — the apiClient interceptor
      // already shows "Server error" toast for 500 responses
      const isHttpError = error && typeof error === 'object' && 'response' in error
      if (!isHttpError) {
        toast.error('Failed to save profile. Please try again.')
      }
    }
  }

  const handleProfileSaveDraft = async (profileData: Partial<UserProfileData>) => {
    try {
      // Filter out empty/incomplete qualifications (professional certs don't need institution)
      const validQualifications = (profileData.qualifications || [])
        .filter(q => q.type === 'professional' ? q.professionalBody : (q.institution && q.institution.trim()))
        .map(q => ({
          qualification_type: q.type,
          institution: q.institution || '',
          country: q.country || 'south_africa',
          
          // Academic details
          qualification_name: q.qualificationName?.trim() || undefined,
          cesm_category: q.cesmCategory || undefined,
          field_of_study: q.fieldOfStudy?.trim() || undefined,

          // NQF
          nqf_level: q.nqfLevel || undefined,

          // Completion status
          year_completed: q.yearCompleted || undefined,
          in_progress: q.inProgress || false,
          expected_completion_year: q.expectedCompletionYear || undefined,

          // International
          international_equivalency: q.internationalEquivalency || undefined,
          saqa_evaluated: q.saqaEvaluated || false,
          saqa_reference_number: q.saqaReferenceNumber || undefined,

          // Professional cert
          professional_body: q.professionalBody || undefined,
          membership_number: q.membershipNumber || undefined,
          membership_status: q.membershipStatus || undefined,

          // Research component
          thesis: q.thesis || false,
          thesis_title: q.thesisTitle || undefined,

          // Verification
          verification_document_url: q.verificationDocument || undefined
        }));

      // Filter out empty/incomplete career entries
      const validCareerHistory = (profileData.careerHistory || [])
        .filter(c => c.organization && c.organization.trim() && c.position && c.position.trim() && c.startDate)
        .map(c => ({
          organization: c.organization,
          position: c.position,
          start_date: c.startDate,
          end_date: c.endDate,
          organization_type: c.organizationType,
          direct_reports: c.directReports,
          extended_team_size: c.extendedTeamSize,
          budget_responsibility: c.budgetResponsibility,
          budget_currency: c.budgetCurrency,
          key_achievements: c.keyAchievements || [],
          skills_developed: c.skillsDeveloped || []
        }));

      // Save profile data to backend (as partial/draft)
      await profileService.saveCompleteProfile({
        qualifications: validQualifications,
        career_history: validCareerHistory,
        professional_context: {
          areas_of_expertise: profileData.professionalContext?.areasOfExpertise || [],
          language_proficiencies: (profileData.professionalContext?.languageProficiencies || []).map(l => ({
            language: l.language,
            level: l.proficiency || 'intermediate'
          })),
          geographic_experience: profileData.professionalContext?.geographicExperience || [],
          industry_experience: profileData.professionalContext?.industryExperience || [],
          completion_status: 'partial'
        }
      })
      
      // Update local state with saved profile data (don't navigate)
      setOnboardingState(prev => ({
        ...prev,
        profileData
      }))
      
      // Note: Toast is shown in ProfileCompletionStep after awaiting this call
    } catch (error) {
      console.error('Failed to save draft:', error)
      // Re-throw so ProfileCompletionStep.handleSaveDraft can handle the error toast
      throw error
    }
  }

  const handleTourComplete = () => {
    setOnboardingState(prev => ({
      ...prev,
      tourCompleted: true
    }))
    goToNextStep()
  }

  const handleTourSkip = () => {
    setOnboardingState(prev => ({
      ...prev,
      tourSkipped: true
    }))
    goToNextStep()
  }

  const estimatedDuration = scenario === 'first-time-user' ? 15 : scenario === 'annual-profile-update' ? 5 : 10

  // Clear the restore flag after first paint so subsequent navigations animate normally
  useEffect(() => {
    if (!isRestoring && didRestore.current) {
      // Use a microtask so the first render completes without animation
      requestAnimationFrame(() => { didRestore.current = false })
    }
  }, [isRestoring])

  // While restoring, render nothing — the parent page already shows a loading spinner
  if (isRestoring) return null

  // Skip entrance animations when resuming from a saved step
  const skipAnimation = didRestore.current

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 py-8 px-8">
      <div className="container mx-auto max-w-6xl space-y-8">
        {/* Progress Bar */}
        <motion.div
          initial={skipAnimation ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: skipAnimation ? 0 : 0.5 }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6 space-y-3"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Badge className="bg-heritage-blue text-white">
                Step {currentStepIndex + 1} of {stepFlow.length}
              </Badge>
              <span className="text-sm font-medium text-heritage-blue">
                {stepFlow[currentStepIndex] === 'fo-confirmation'
                  ? 'Profile Confirmation'
                  : stepFlow[currentStepIndex].replace('-', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
            </div>
          </div>
          <Progress value={(completedSteps.length / stepFlow.length) * 100} className="h-3" />
        </motion.div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={skipAnimation ? false : { opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={skipAnimation ? { duration: 0 } : { duration: 0.4, type: "spring", stiffness: 100 }}
            className="bg-white/90 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl overflow-hidden"
          >
            <div className="p-8">
              {currentStep === 'welcome' && (
                <WelcomeStep
                  scenario={scenario}
                  employeeName={`${foData.firstName} ${foData.lastName}`}
                  jobTitle={foData.jobTitle}
                  onNext={goToNextStep}
                  estimatedDuration={estimatedDuration}
                />
              )}

              {currentStep === 'fo-confirmation' && (
                <FOConfirmationStep
                  foData={foData}
                  onConfirm={handleFOConfirm}
                  onReportIncorrect={handleFOReportIncorrect}
                  onBack={goToPreviousStep}
                />
              )}

              {currentStep === 'profile-completion' && (
                <ProfileCompletionStep
                  initialData={onboardingState.profileData || undefined}
                  onSave={handleProfileSave}
                  onSaveDraft={handleProfileSaveDraft}
                  onSkip={scenario === 'first-time-user' ? undefined : goToNextStep}
                  allowSkip={scenario !== 'first-time-user'}
                  onBack={goToPreviousStep}
                />
              )}

              {currentStep === 'manager-orientation' && (
                <ManagerOrientationStep
                  directReportsCount={foData.directReports}
                  directReportsList={foData.directReportsList}
                  employeeName={`${foData.firstName} ${foData.lastName}`}
                  employeeId={foData.employeeId}
                  employeeUserId={foData.userId}
                  lineManagerName={foData.lineManagerName}
                  onNext={goToNextStep}
                  onBack={goToPreviousStep}
                  onReportTeamIssue={handleTeamReportIncorrect}
                />
              )}

              {currentStep === 'complete' && (
                <OnboardingCompleteStep
                  employeeName={`${foData.firstName} ${foData.lastName}`}
                  isManager={foData.directReports > 0}
                  onComplete={handleComplete}
                />
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}
